import type {SafeHTMLString} from '@github-ui/safe-html'
import {sanitize} from '@github-ui/sanitize'
import type {Comment} from '@github-ui/diff-lines/conversation-types'
import {CommentReviewVariantType} from '@github-ui/diff-lines/conversation-types'
import {VALUES} from '@github-ui/commenting/Values'
import {isSuggestionCommitMessageGenerationEligible} from '@github-ui/diff-lines/helpers/create-diff-hunk-references-from-entries'

/**
 * Per-item `bodyHTML` cap. Covers nearly every review comment while keeping the
 * worst case (~1.6 MB at the 50-item ceiling) well under sessionStorage's ~5 MB
 * budget. Applied here, not in storage, so in-memory and reloaded items render
 * identically.
 */
export const COMMENT_SNAPSHOT_BODY_CAP_BYTES = 32 * 1024

export type CommentSnapshotAuthor = {login: string; avatarUrl?: string; url?: string; isCopilot?: boolean}

/** Strips a trailing `[bot]` marker from a bot login, mirroring the thread header. */
function formatBotAuthorLogin(login: string): string {
  return login.replace(/\[bot\]$/i, '')
}

/**
 * Resolves the snapshot's display author to match the source thread, mirroring
 * `AutomatedReviewThreadComment`: Copilot comments show `Copilot`, other bots
 * have their `[bot]` marker stripped, and a missing author falls back to ghost.
 *
 * `isCopilot` matches the identity rendered by the thread header: the legacy `copilot`
 * variant, or an automated comment flagged `aiAuthored`. Gating on that (not `kind`)
 * keeps the Copilot icon off other automated authors.
 *
 * `url` is the author's profile link. The diff comment data doesn't reliably carry
 * `author.url` (the thread header itself derives the profile link from the login —
 * `ActivityHeader.tsx:154`), so for human authors we fall back to `/{login}` to match.
 * Automated authors keep whatever `author.url` the data provides (often none), rather
 * than fabricating a `/{login}` link that would 404 for a bot slug.
 */
function resolveSnapshotAuthor(comment: CommentSnapshotInput): CommentSnapshotAuthor {
  const isCopilot =
    comment.reviewVariantType === CommentReviewVariantType.Copilot || (comment.automatedComment?.aiAuthored ?? false)
  if (comment.automatedComment) {
    const login = comment.automatedComment.aiAuthored
      ? 'Copilot'
      : comment.author
        ? formatBotAuthorLogin(comment.author.login)
        : VALUES.ghostUser.login
    return {login, avatarUrl: comment.author?.avatarUrl, url: comment.author?.url, isCopilot}
  }
  return comment.author
    ? {
        login: comment.author.login,
        avatarUrl: comment.author.avatarUrl,
        url: comment.author.url || `/${comment.author.login}`,
        isCopilot,
      }
    : {login: VALUES.ghostUser.login, isCopilot}
}

export type CommentSnapshot = {
  author: CommentSnapshotAuthor
  createdAt: string
  bodyHTML: SafeHTMLString
  bodyTruncated: boolean
  /** Per-comment eligibility only; generation still requires current PR-level authorization. */
  commitMessageGenerationEligible?: boolean
  /**
   * The comment's server-issued permalink (`Comment.url`), captured so an item that
   * has no `sourceUri` still has a durable navigation target once it is marked
   * outdated. Optional: items persisted before this field existed simply lack it.
   */
  commentUrl?: string
}

/** Snapshot for `human-with-suggestion` items, which also carry the extracted blob HTML. */
export type CommentSnapshotWithSuggestion = CommentSnapshot & {suggestionBlobHTML: SafeHTMLString}

/** The subset of a {@link Comment} that {@link buildCommentSnapshot} reads. */
export type CommentSnapshotInput = Pick<
  Comment,
  'author' | 'createdAt' | 'bodyHTML' | 'automatedComment' | 'reviewVariantType'
> &
  Partial<Pick<Comment, 'url'>>

export type BuildCommentSnapshotOptions = {
  /**
   * When provided, the returned snapshot includes `suggestionBlobHTML` extracted
   * from the `div.js-suggested-changes-blob` at this index within the comment's
   * server-rendered body. Used for `human-with-suggestion` items.
   */
  suggestionIndex?: number
}

export function buildCommentSnapshot(
  comment: CommentSnapshotInput,
  opts: {suggestionIndex: number},
): CommentSnapshotWithSuggestion
export function buildCommentSnapshot(comment: CommentSnapshotInput, opts?: BuildCommentSnapshotOptions): CommentSnapshot
/**
 * Builds the snapshot the Manage batch panel renders in-place, centralizing the
 * body source, ghost-author fallback, size cap, and the single `SafeHTMLString`
 * ingress cast.
 *
 * Body source is `automatedComment.message` for CCR (its `bodyHTML` is only a
 * summary line — see `AutomatedReviewThreadComment`), else `comment.bodyHTML`;
 * both are server-sanitized, so the cast here is the correct ingress point.
 * Over-cap bodies become `''` with `bodyTruncated` set (never clipped mid-tag,
 * which wouldn't be sanitization-safe).
 *
 * Also captures `comment.url` as `commentUrl` — the durable navigation target for
 * an item that carries no `sourceUri` but is later marked outdated by a head change
 * (see `useCommentNavigation`). An empty url is normalized to `undefined` so the
 * fallback is either usable or absent, never a falsy string.
 *
 * With `opts.suggestionIndex`, also carries the extracted `suggestionBlobHTML` and
 * returns `bodyHTML` with every suggestion blob removed, so the panel renders the
 * selected suggestion once in its own card. See {@link extractSuggestionBlob}.
 */
export function buildCommentSnapshot(
  comment: CommentSnapshotInput,
  opts: BuildCommentSnapshotOptions = {},
): CommentSnapshot | CommentSnapshotWithSuggestion {
  const author = resolveSnapshotAuthor(comment)
  const commitMessageGenerationEligible =
    isSuggestionCommitMessageGenerationEligible(comment.automatedComment) || undefined

  const rawBody = comment.automatedComment?.message ?? comment.bodyHTML ?? ''

  if (opts.suggestionIndex !== undefined) {
    // Extract the selected suggestion first, then cap only the leftover prose. A large but
    // valid suggestion keeps its blob card (the blob carries its own storage-quota fallback)
    // instead of the whole snapshot being dropped when the raw body exceeds the cap.
    const {bodyHTML: bodyWithoutBlob, suggestionBlobHTML} = extractSuggestionBlob(rawBody, opts.suggestionIndex)
    const bodyTruncated = byteLength(bodyWithoutBlob) > COMMENT_SNAPSHOT_BODY_CAP_BYTES
    // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- bodyWithoutBlob derives from the server-sanitized bodyHTML; ingress assertion point.
    const bodyHTML = (bodyTruncated ? '' : bodyWithoutBlob) as SafeHTMLString
    return {
      author,
      createdAt: comment.createdAt,
      bodyHTML,
      bodyTruncated,
      commitMessageGenerationEligible,
      commentUrl: comment.url || undefined,
      suggestionBlobHTML,
    }
  }

  const bodyTruncated = byteLength(rawBody) > COMMENT_SNAPSHOT_BODY_CAP_BYTES
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- automatedComment.message / bodyHTML are sanitized server-side; this is the data-ingress assertion point.
  const bodyHTML = (bodyTruncated ? '' : rawBody) as SafeHTMLString

  return {
    author,
    createdAt: comment.createdAt,
    bodyHTML,
    bodyTruncated,
    commitMessageGenerationEligible,
    commentUrl: comment.url || undefined,
  }
}

const SUGGESTION_BLOB_SELECTOR = 'div.js-suggested-changes-blob'

/**
 * Extracts the `suggestionIndex`-th suggestion blob from a human comment's `bodyHTML`,
 * returning it separately as `suggestionBlobHTML` stripped of its chrome (see
 * {@link stripSuggestionBlobChrome}) so the panel renders it once in its own card. All
 * suggestion blobs are removed from the returned `bodyHTML` — the selected one is promoted
 * to its own card, and the rest would otherwise render with stale inline chrome (and count
 * against the prose-only cap), so none should remain in the body.
 *
 * Client-only: on a DOM-less host it degrades to the untouched body and an empty blob.
 * When the captured index is out of range but other blobs remain, all blobs are still
 * stripped (empty card) so no sibling suggestion leaks inline.
 */
function extractSuggestionBlob(
  rawBody: string,
  suggestionIndex: number,
): {bodyHTML: SafeHTMLString; suggestionBlobHTML: SafeHTMLString} {
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- rawBody is server-sanitized bodyHTML; ingress assertion point.
  const untouched = {bodyHTML: rawBody as SafeHTMLString, suggestionBlobHTML: emptySafeHTML()}

  if (typeof DOMParser === 'undefined') return untouched

  const doc = new DOMParser().parseFromString(rawBody, 'text/html')
  const blobs = doc.body.querySelectorAll(SUGGESTION_BLOB_SELECTOR)

  // No blobs at all: nothing to extract, and the body carries no suggestion to leak.
  if (blobs.length === 0) return untouched

  const blob = blobs[suggestionIndex]

  // Index out of range — the comment was edited to have fewer blocks after batching. We can't
  // promote a card, but still strip every blob: leaving them inline would re-render sibling
  // items' suggestions.
  if (!blob) {
    for (const suggestionBlob of blobs) suggestionBlob.remove()
    return {
      bodyHTML: sanitizeExtractedHtml(doc.body.innerHTML),
      suggestionBlobHTML: emptySafeHTML(),
    }
  }

  const blobHTML = stripSuggestionBlobChrome(blob).outerHTML
  for (const suggestionBlob of blobs) suggestionBlob.remove()

  // Reading back `innerHTML`/`outerHTML` re-serializes DOM-decoded content, which the
  // output-safety rules treat as untrusted regardless of the server-sanitized source. Run
  // both through the sanitizer — the same call the storage-reload path uses — so the initial
  // in-memory render is protected too, and matches the reloaded render byte-for-byte.
  return {
    bodyHTML: sanitizeExtractedHtml(doc.body.innerHTML),
    suggestionBlobHTML: sanitizeExtractedHtml(blobHTML),
  }
}

function sanitizeExtractedHtml(value: string): SafeHTMLString {
  return sanitize(value, {RETURN_TRUSTED_TYPE: false})
}

/**
 * Strips the blob's own card chrome (border/rounding/margin classes + the "Suggested
 * change" header) so only the `.blob-wrapper` diff table remains and renders
 * seamlessly in the shared card. Primer's border/flex utilities are `!important`, so
 * the chrome must be removed here rather than overridden in CSS. Degrades to the
 * untouched blob when there's no wrapper.
 */
function stripSuggestionBlobChrome(blob: Element): Element {
  blob.classList.remove('border', 'rounded-2', 'my-2')

  const wrapper = blob.querySelector(':scope > .blob-wrapper')
  if (wrapper) {
    for (const child of Array.from(blob.children)) {
      if (child !== wrapper) child.remove()
    }
  }

  return blob
}

function emptySafeHTML(): SafeHTMLString {
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- The empty string is trivially safe HTML.
  return '' as SafeHTMLString
}

const textEncoder = new TextEncoder()

function byteLength(value: string): number {
  return textEncoder.encode(value).byteLength
}
